<template>
  <section v-if="!loading && category_items.length" class="categories__area light-bg-s pt-20 pb-10">
    <div class="container">
      <div class="row">
        <div class="col-xl-12">
          <div class="section__head d-flex justify-content-between mb-30">
            <div class="section__title section__title-2">
              <h5 class="st-titile">Popular Categories</h5>
            </div>
            <div class="button-wrap button-wrap-2">
              <router-link to="/shop">See All Product <i class="fal fa-chevron-right"></i></router-link>
            </div>
          </div>
        </div>
      </div>
      <div class="row g-2 g-md-3">
        <div
          v-for="item in category_items"
          :key="item.cat_id"
          class="col-6 col-md-4 col-lg-3 col-xl-2"
        >
          <router-link
            :to="'/shop?category=' + item.cat_id"
            class="top-cat-card text-decoration-none d-flex align-items-center"
          >
            <div
              class="top-cat-card__icon"
              aria-hidden="true"
            >
              <i :class="item.icon"></i>
            </div>
            <div class="categories__content">
              <h6>
                <span class="top-cat-card__title">{{ item.cat_name }}</span>
              </h6>
              <p>({{ item.products_count }} Products)</p>
            </div>
          </router-link>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axiosInstance from '@/services/axios'

const loading = ref(true)
const category_items = ref([])

/** Pick a recognisable icon per category name. */
function iconForCategory(name) {
  const n = String(name || '').toLowerCase()
  if (n.includes('laptop') || n.includes('computer'))        return 'fas fa-laptop'
  if (n.includes('mobile') || n.includes('phone'))            return 'fas fa-mobile-alt'
  if (n.includes('tablet'))                                   return 'fas fa-tablet-alt'
  if (n.includes('accessor'))                                 return 'fas fa-headphones-alt'
  if (n.includes('party') || n.includes('box'))               return 'fas fa-gift'
  if (n.includes('service') || n.includes('repair'))          return 'fas fa-tools'
  if (n.includes('digital') || n.includes('electronic'))      return 'fas fa-plug'
  if (n.includes('camera'))                                   return 'fas fa-camera'
  if (n.includes('headphone') || n.includes('audio') || n.includes('speaker')) return 'fas fa-headphones'
  if (n.includes('watch') || n.includes('smart'))             return 'fas fa-clock'
  if (n.includes('tv') || n.includes('television') || n.includes('monitor')) return 'fas fa-tv'
  if (n.includes('gaming') || n.includes('game'))             return 'fas fa-gamepad'
  if (n.includes('other'))                                    return 'fas fa-boxes'
  return 'fas fa-th-large'
}

async function fetchPopularTopCategories() {
  loading.value = true
  try {
    const { data } = await axiosInstance.get('/popular-top-categories')
    const list = Array.isArray(data) ? data : []
    category_items.value = list.map((row) => ({
      cat_id: row.cat_id,
      cat_name: row.cat_name,
      products_count: row.products_count ?? 0,
      icon: iconForCategory(row.cat_name),
    }))
  } catch {
    category_items.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchPopularTopCategories()
})
</script>

<style scoped>
.top-cat-card {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 16px;
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.04);
  gap: 14px;
  height: 100px;
  padding: 16px 18px;
  position: relative;
  text-align: left;
}

.top-cat-card:hover {
  transform: translateY(-4px);
  border-color: var(--fe-accent, #fcbe00);
  box-shadow: 0 12px 32px var(--fe-accent-soft, rgba(252, 190, 0, 0.15));
}

.top-cat-card__icon {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--fe-accent, #fcbe00);
  color: #fff;
  font-size: 1.35rem;
  box-shadow: 0 6px 18px var(--fe-accent-soft, rgba(252, 190, 0, 0.25));
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
}

.top-cat-card:hover .top-cat-card__icon {
  transform: scale(1.08);
  background: var(--fe-accent-hover, #e5ac00);
  box-shadow: 0 8px 24px var(--fe-accent-soft, rgba(252, 190, 0, 0.35));
}

.top-cat-card .categories__content {
  position: static !important;
  top: auto !important;
  transform: none !important;
  padding: 0 !important;
  margin-top: 0 !important;
  width: auto;
  min-width: 0;
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
}

.top-cat-card .categories__content h6 {
  margin: 0 !important;
}

.top-cat-card__title {
  color: #1a1a2e;
  font-weight: 700;
  font-size: 14px;
  line-height: 1.3;
  text-decoration: none;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-clamp: 2;
  letter-spacing: -0.01em;
}

.top-cat-card .categories__content p {
  margin-top: 4px;
  margin-bottom: 0;
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
  opacity: 1 !important;
  line-height: 1;
}

@media (max-width: 575px) {
  .top-cat-card {
    height: 90px;
    padding: 12px 14px;
    gap: 10px;
  }
  .top-cat-card__icon {
    width: 48px;
    height: 48px;
    font-size: 1.15rem;
  }
  .top-cat-card__title {
    font-size: 12.5px;
  }
}
</style>